import React, { useState } from 'react';
import classNames from 'classnames';
import { noop } from '@moda/portal-stanchions';
import { ForgotPassword } from '../ForgotPassword';
import { Register, RegisterFormValues } from './Register';
import { SignIn, SignInFormValues } from './SignIn';

import './Authentication.scss';

export type DefaultValues = Partial<SignInFormValues> | Partial<RegisterFormValues>;

type AuthenticationProps = {
  fullPage?: boolean;
  modal?: boolean;
  defaultValues?: DefaultValues;
  onAuth?: () => void;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
};

const SIGN_IN_TAB = 'SIGN IN';
const REGISTER_TAB = 'REGISTER';
const FORGOT_PASSWORD_TAB = 'FORGOT PASSWORD';

const isSignInFormValues = (values?: DefaultValues): values is Partial<SignInFormValues> =>
  values != null && 'signInEmail' in values;

const isRegisterFormValues = (values?: DefaultValues): values is Partial<RegisterFormValues> =>
  values != null && 'registerEmail' in values;

export const Authentication: React.FC<AuthenticationProps> = ({
  fullPage = false,
  modal = false,
  defaultValues,
  onAuth = noop,
  showAuthModal,
  hideAuthModal
}) => {
  const [activeTab, setActiveTab] = useState(
    isRegisterFormValues(defaultValues) ? REGISTER_TAB : SIGN_IN_TAB
  );
  const registerActive = activeTab === REGISTER_TAB;
  const signInActive = activeTab === SIGN_IN_TAB;
  const forgotPasswordActive = activeTab === FORGOT_PASSWORD_TAB;
  const handleClickForgotPassword = !fullPage ? () => setActiveTab(FORGOT_PASSWORD_TAB) : undefined;
  const showForgotPassword = !fullPage && forgotPasswordActive;
  const showSignInRegister = fullPage || !forgotPasswordActive;

  return (
    <div
      className={classNames('Authentication', {
        'Authentication--fullPage': fullPage,
        'Authentication--modal': modal
      })}
      data-testid="authentication"
    >
      <div
        className={classNames('Authentication__header', {
          'Authentication__header--hidden': forgotPasswordActive
        })}
      >
        <button
          className={classNames('Authentication__tab', {
            'Authentication__tab--active': registerActive
          })}
          data-testid="registerTab"
          onClick={() => setActiveTab(REGISTER_TAB)}
        >
          {REGISTER_TAB}
        </button>
        <button
          className={classNames('Authentication__tab', {
            'Authentication__tab--active': signInActive
          })}
          data-testid="signInTab"
          onClick={() => setActiveTab(SIGN_IN_TAB)}
        >
          {SIGN_IN_TAB}
        </button>
      </div>

      <div className="Authentication__content">
        {showSignInRegister && (
          <>
            <Register
              className={classNames('Authentication__section', {
                'Authentication__section--active': registerActive
              })}
              defaultValues={isRegisterFormValues(defaultValues) ? defaultValues : undefined}
              onAuth={onAuth}
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              showCancelButton={!fullPage}
            />
            <SignIn
              className={classNames('Authentication__section', {
                'Authentication__section--active': signInActive
              })}
              defaultValues={isSignInFormValues(defaultValues) ? defaultValues : undefined}
              onAuth={onAuth}
              onClickForgotPassword={handleClickForgotPassword}
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              showCancelButton={!fullPage}
            />
          </>
        )}

        {showForgotPassword && (
          <ForgotPassword
            className={classNames('Authentication__section', {
              'Authentication__section--active': forgotPasswordActive
            })}
            onClickSignIn={() => setActiveTab(SIGN_IN_TAB)}
          />
        )}
      </div>
    </div>
  );
};
